import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircleDashed, CheckCircle2 } from "lucide-react";
import { ResTecnicoAccess } from "../../components/RoleBasedAccess";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useDocument } from "../../hooks/useDocument";
import { useFirestore } from "../../hooks/useFirestore";
import "../contrattiClienti/ContrattiClienti.css";

const normalizeStr = (value) => String(value ?? "").trim().toLowerCase();

const ensureId = (item) => ({
  id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
  ...item,
});

const normalizeManutenzioni = (cliente) => {
  const getMonthIndexFromDateString = (value) => {
    if (!value || typeof value !== "string") return 0;
    const parts = value.split("-");
    if (parts.length < 2) return 0;
    const monthIndex = Number(parts[1]) - 1;
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return 0;
    }
    return monthIndex;
  };

  const normalizeOffsetByMonth = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const out = {};
    Object.entries(value).forEach(([from, to]) => {
      if (typeof from !== "string" || typeof to !== "string") return;
      if (!/^\d{4}-\d{2}$/.test(from) || !/^\d{4}-\d{2}$/.test(to)) return;
      out[from] = to;
    });
    return out;
  };

  const normalizeAttivita = (m) => {
    const fromArray = Array.isArray(m?.attivita) ? m.attivita : [];
    if (fromArray.length > 0) {
      return fromArray.map((a) => ({
        id: a?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        descrizione: a?.descrizione || "",
        frequenza: a?.frequenza || "",
        meseInizio:
          typeof a?.meseInizio === "number" &&
          a.meseInizio >= 0 &&
          a.meseInizio <= 11
            ? a.meseInizio
            : getMonthIndexFromDateString(a?.dataInizio || a?.scadenza),
        registroInterventi: Array.isArray(a?.registroInterventi)
          ? a.registroInterventi.map(ensureId)
          : [],
        pianificazioneOffsetByMonth: normalizeOffsetByMonth(
          a?.pianificazioneOffsetByMonth,
        ),
      }));
    }

    const legacyInterventi = Array.isArray(m?.registroInterventi)
      ? m.registroInterventi.map(ensureId)
      : [];

    return [
      {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        descrizione:
          m?.descrizione || m?.impiantoSpeciale || "Attivita manutenzione",
        frequenza: m?.frequenza || "",
        meseInizio: getMonthIndexFromDateString(m?.scadenza),
        registroInterventi: legacyInterventi,
        pianificazioneOffsetByMonth: {},
      },
    ];
  };

  const fromArray = Array.isArray(cliente?.manutenzioni)
    ? cliente.manutenzioni
    : [];

  if (fromArray.length > 0) {
    return fromArray.map((m) => ({
      id: m?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      attivo: Boolean(m?.attivo),
      sede: m?.sede || "",
      descrizione: m?.descrizione || m?.impiantoSpeciale || "",
      importo: m?.importo ?? "",
      frequenza: m?.frequenza || "",
      scadenza: m?.scadenza || "",
      settoreId: m?.settoreId || "",
      settoreNome: m?.settoreNome || "",
      attivita: normalizeAttivita(m),
      registroFatturazioni: Array.isArray(m?.registroFatturazioni)
        ? m.registroFatturazioni.map(ensureId)
        : [],
    }));
  }

  const legacy = cliente?.manutenzione;
  if (!legacy) return [];

  return [
    {
      id: legacy?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      attivo: Boolean(legacy?.attivo),
      sede: legacy?.sede || "",
      descrizione: legacy?.descrizione || legacy?.impiantoSpeciale || "",
      importo: legacy?.importo ?? "",
      frequenza: legacy?.frequenza || "",
      scadenza: legacy?.scadenza || "",
      settoreId: legacy?.settoreId || "",
      settoreNome: legacy?.settoreNome || "",
      attivita: normalizeAttivita(legacy),
      registroFatturazioni: Array.isArray(legacy?.registroFatturazioni)
        ? legacy.registroFatturazioni.map(ensureId)
        : [],
    },
  ];
};

export default function TabellaManutenzioni() {
  const { user } = useAuthContext();
  const { document: userProfile } = useDocument("users", user?.uid);
  const { documents: clienti, error } = useCollection("clienti");
  const { documents: settoriDocs } = useCollection("settori");
  const { updateDocument } = useFirestore("clienti");
  const [plannerYear, setPlannerYear] = useState(new Date().getFullYear());
  const [plannerOnlyActive, setPlannerOnlyActive] = useState(true);
  const [selectedPlannerCell, setSelectedPlannerCell] = useState(null);
  const [plannerOpenByCliente, setPlannerOpenByCliente] = useState({});
  const [plannerOffsetBaseKey, setPlannerOffsetBaseKey] = useState("");
  const [plannerOffsetTargetKey, setPlannerOffsetTargetKey] = useState("");
  const [plannerOffsetSaving, setPlannerOffsetSaving] = useState(false);
  const [plannerOffsetFeedback, setPlannerOffsetFeedback] = useState(null);

  const mySettore = useMemo(() => {
    const settoreRaw = userProfile?.anagrafica?.settore;
    const settoreNorm = normalizeStr(settoreRaw);
    const isRoby = settoreNorm === "roby";
    const docs = Array.isArray(settoriDocs) ? settoriDocs : [];

    if (isRoby) {
      return { isRoby: true, settoreId: "", settoreNome: "", settoreNorm };
    }

    const matched = docs.find((s) => {
      const idNorm = normalizeStr(s?.id);
      const nomeNorm = normalizeStr(s?.nome);
      if (!settoreNorm) return false;
      if (idNorm && idNorm === settoreNorm) return true;
      if (nomeNorm && nomeNorm === settoreNorm) return true;
      if (nomeNorm && nomeNorm.includes(settoreNorm)) return true;
      if (settoreNorm && settoreNorm.includes(nomeNorm)) return true;
      return false;
    });

    return {
      isRoby: false,
      settoreId: matched?.id || "",
      settoreNome: matched?.nome || "",
      settoreNorm,
    };
  }, [settoriDocs, userProfile?.anagrafica?.settore]);

  const canoneMatchesSettore = useCallback(
    (canone) => {
      if (mySettore?.isRoby) return true;
      if (!mySettore?.settoreNorm) return false;

      const canoneSettoreIdNorm = normalizeStr(canone?.settoreId);
      const canoneSettoreNomeNorm = normalizeStr(canone?.settoreNome);
      const mySettoreIdNorm = normalizeStr(mySettore?.settoreId);
      const mySettoreNomeNorm = normalizeStr(mySettore?.settoreNome);

      if (mySettoreIdNorm && canoneSettoreIdNorm === mySettoreIdNorm)
        return true;
      if (
        canoneSettoreIdNorm &&
        canoneSettoreIdNorm === mySettore.settoreNorm
      )
        return true;
      if (mySettoreNomeNorm && canoneSettoreNomeNorm === mySettoreNomeNorm)
        return true;
      if (
        canoneSettoreNomeNorm &&
        canoneSettoreNomeNorm === mySettore.settoreNorm
      )
        return true;
      if (
        canoneSettoreNomeNorm &&
        mySettoreNomeNorm &&
        (canoneSettoreNomeNorm.includes(mySettoreNomeNorm) ||
          mySettoreNomeNorm.includes(canoneSettoreNomeNorm))
      ) {
        return true;
      }

      return false;
    },
    [mySettore],
  );

  const mesi = useMemo(
    () => [
      "Gen",
      "Feb",
      "Mar",
      "Apr",
      "Mag",
      "Giu",
      "Lug",
      "Ago",
      "Set",
      "Ott",
      "Nov",
      "Dic",
    ],
    [],
  );

  const parseYearMonthFromDateString = (value) => {
    if (!value || typeof value !== "string") return null;
    const parts = value.split("-");
    if (parts.length < 2) return null;
    const year = Number(parts[0]);
    const monthIndex = Number(parts[1]) - 1;
    if (!Number.isFinite(year) || year < 1900) return null;
    if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return null;
    }
    return { year, monthIndex };
  };

  const toMonthKey = (year, monthIndex) => {
    const mm = String((monthIndex ?? 0) + 1).padStart(2, "0");
    return `${year}-${mm}`;
  };

  const monthKeyToLabel = (monthKey) => {
    if (!monthKey || typeof monthKey !== "string") return "—";
    const parts = monthKey.split("-");
    if (parts.length < 2) return monthKey;
    const year = parts[0];
    const monthIndex = Number(parts[1]) - 1;
    const label = mesi[monthIndex] || parts[1];
    return `${label} ${year}`;
  };

  const isMonthExpected = (attivita, manutenzione, year, monthIndex) => {
    if (!manutenzione || manutenzione.attivo === false) return false;
    if (!attivita || !attivita.frequenza) return false;

    const hasExplicitStart =
      typeof attivita.meseInizio === "number" &&
      attivita.meseInizio >= 0 &&
      attivita.meseInizio <= 11;

    let startMonthIndex = hasExplicitStart ? attivita.meseInizio : 0;

    const anchor = parseYearMonthFromDateString(manutenzione.scadenza);
    if (!hasExplicitStart && !anchor) return false;
    if (!hasExplicitStart && anchor && anchor.year <= year) {
      startMonthIndex = anchor.monthIndex;
    }

    const freq = String(attivita.frequenza || "").toLowerCase();
    if (freq === "mensile") return true;
    if (freq === "trimestrale") {
      return (monthIndex - startMonthIndex + 12) % 3 === 0;
    }
    if (freq === "quadrimestrale") {
      return (monthIndex - startMonthIndex + 12) % 4 === 0;
    }
    if (freq === "semestrale") {
      return (monthIndex - startMonthIndex + 12) % 6 === 0;
    }
    if (freq === "annuale") return monthIndex === startMonthIndex;
    return false;
  };

  const plannerRows = useMemo(() => {
    const list = Array.isArray(clienti) ? clienti : [];
    const rows = [];
    list.forEach((c) => {
      const manutenzioni = normalizeManutenzioni(c);
      manutenzioni.forEach((m) => {
        if (!canoneMatchesSettore(m)) return;
        if (plannerOnlyActive && !Boolean(m.attivo)) return;
        const attivita = Array.isArray(m.attivita) ? m.attivita : [];
        attivita.forEach((a) => {
          rows.push({
            rowId: `${c.id}__${m.id}__${a.id}`,
            clienteId: c.id,
            clienteNome: c?.nome || "—",
            manutenzioneId: m.id,
            sede: m.sede || "",
            scadenza: m.scadenza || "",
            attivo: Boolean(m.attivo),
            attivitaId: a.id,
            attivitaDescrizione: a.descrizione || "",
            attivitaFrequenza: a.frequenza || "",
            attivitaMeseInizio:
              typeof a.meseInizio === "number" ? a.meseInizio : null,
            attivitaOffsetByMonth:
              a?.pianificazioneOffsetByMonth &&
              typeof a.pianificazioneOffsetByMonth === "object" &&
              !Array.isArray(a.pianificazioneOffsetByMonth)
                ? a.pianificazioneOffsetByMonth
                : {},
            registroInterventi: Array.isArray(a.registroInterventi)
              ? a.registroInterventi
              : [],
            __cliente: c,
          });
        });
      });
    });
    return rows;
  }, [clienti, plannerOnlyActive, canoneMatchesSettore]);

  const plannerGroups = useMemo(() => {
    const map = new Map();
    plannerRows.forEach((row) => {
      const key = row?.clienteId || row?.clienteNome || "unknown";
      const current = map.get(key);
      if (current) {
        current.rows.push(row);
      } else {
        map.set(key, {
          key,
          clienteNome: row?.clienteNome || "—",
          rows: [row],
        });
      }
    });

    const groups = Array.from(map.values());
    groups.sort((a, b) =>
      String(a.clienteNome || "").localeCompare(
        String(b.clienteNome || ""),
        "it",
        { sensitivity: "base" },
      ),
    );

    groups.forEach((group) => {
      group.rows.sort((ra, rb) => {
        const sedeCmp = String(ra?.sede || "").localeCompare(
          String(rb?.sede || ""),
          "it",
          { sensitivity: "base" },
        );
        if (sedeCmp !== 0) return sedeCmp;
        return String(ra?.attivitaDescrizione || "").localeCompare(
          String(rb?.attivitaDescrizione || ""),
          "it",
          { sensitivity: "base" },
        );
      });
    });

    return groups;
  }, [plannerRows]);

  const togglePlannerCliente = (key) => {
    setPlannerOpenByCliente((prev) => {
      const safePrev = prev || {};
      const current = safePrev[key];
      const isOpen = typeof current === "boolean" ? current : true;
      return { ...safePrev, [key]: !isOpen };
    });
  };

  const getMonthStats = (row, year, monthIndex) => {
    const currentMonthKey = toMonthKey(year, monthIndex);
    const offsets =
      row?.attivitaOffsetByMonth &&
      typeof row.attivitaOffsetByMonth === "object" &&
      !Array.isArray(row.attivitaOffsetByMonth)
        ? row.attivitaOffsetByMonth
        : {};

    const interventi = (row.registroInterventi || []).filter((r) => {
      const ym = parseYearMonthFromDateString(r?.data);
      return ym && ym.year === year && ym.monthIndex === monthIndex;
    });

    const baseExpected = isMonthExpected(
      {
        frequenza: row.attivitaFrequenza,
        meseInizio: row.attivitaMeseInizio,
      },
      { attivo: row.attivo, scadenza: row.scadenza },
      year,
      monthIndex,
    );

    const movedAwayTo =
      baseExpected && offsets[currentMonthKey] ? offsets[currentMonthKey] : "";
    const movedHereFrom =
      Object.entries(offsets).find(([, to]) => to === currentMonthKey)?.[0] ||
      "";

    let expected = baseExpected;
    if (expected && movedAwayTo) expected = false;
    if (movedHereFrom) expected = true;

    return {
      expected,
      baseExpected,
      movedAwayTo,
      movedHereFrom,
      interventi,
      interventiCount: interventi.length,
    };
  };

  useEffect(() => {
    if (!selectedPlannerCell?.row) {
      setPlannerOffsetBaseKey("");
      setPlannerOffsetTargetKey("");
      setPlannerOffsetFeedback(null);
      return;
    }

    const { row, currentMonthKey, baseExpected, movedHereFrom } =
      selectedPlannerCell;
    const baseKey = baseExpected ? currentMonthKey : movedHereFrom;
    const offsets =
      row?.attivitaOffsetByMonth &&
      typeof row.attivitaOffsetByMonth === "object" &&
      !Array.isArray(row.attivitaOffsetByMonth)
        ? row.attivitaOffsetByMonth
        : {};
    const existingTarget = baseKey ? offsets[baseKey] || baseKey : "";

    setPlannerOffsetBaseKey(baseKey || "");
    setPlannerOffsetTargetKey(existingTarget || "");
    setPlannerOffsetFeedback(null);
  }, [selectedPlannerCell]);

  const savePlannerOffset = async () => {
    if (
      !selectedPlannerCell?.row ||
      !plannerOffsetBaseKey ||
      !plannerOffsetTargetKey
    ) {
      return;
    }

    const { row } = selectedPlannerCell;
    const cliente = row.__cliente;
    if (!cliente?.id) return;

    setPlannerOffsetSaving(true);
    setPlannerOffsetFeedback(null);
    try {
      const manutenzioni = normalizeManutenzioni(cliente);
      const updatedManutenzioni = manutenzioni.map((m) => {
        if (!m || m.id !== row.manutenzioneId) return m;
        const attivita = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: attivita.map((a) => {
            if (!a || a.id !== row.attivitaId) return a;
            const current =
              a?.pianificazioneOffsetByMonth &&
              typeof a.pianificazioneOffsetByMonth === "object" &&
              !Array.isArray(a.pianificazioneOffsetByMonth)
                ? { ...a.pianificazioneOffsetByMonth }
                : {};

            if (plannerOffsetTargetKey === plannerOffsetBaseKey) {
              delete current[plannerOffsetBaseKey];
            } else {
              current[plannerOffsetBaseKey] = plannerOffsetTargetKey;
            }

            return { ...a, pianificazioneOffsetByMonth: current };
          }),
        };
      });

      await updateDocument(cliente.id, { manutenzioni: updatedManutenzioni });
      setPlannerOffsetFeedback({
        type: "success",
        message: "Spostamento salvato correttamente.",
      });
    } catch (e) {
      setPlannerOffsetFeedback({
        type: "error",
        message: "Errore durante il salvataggio dello spostamento.",
      });
    } finally {
      setPlannerOffsetSaving(false);
    }
  };

  const deletePlannerOffset = async () => {
    if (!selectedPlannerCell?.row || !plannerOffsetBaseKey) return;

    const { row } = selectedPlannerCell;
    const cliente = row.__cliente;
    if (!cliente?.id) return;

    setPlannerOffsetSaving(true);
    setPlannerOffsetFeedback(null);
    try {
      const manutenzioni = normalizeManutenzioni(cliente);
      const updatedManutenzioni = manutenzioni.map((m) => {
        if (!m || m.id !== row.manutenzioneId) return m;
        const attivita = Array.isArray(m.attivita) ? m.attivita : [];
        return {
          ...m,
          attivita: attivita.map((a) => {
            if (!a || a.id !== row.attivitaId) return a;
            const current =
              a?.pianificazioneOffsetByMonth &&
              typeof a.pianificazioneOffsetByMonth === "object" &&
              !Array.isArray(a.pianificazioneOffsetByMonth)
                ? { ...a.pianificazioneOffsetByMonth }
                : {};
            delete current[plannerOffsetBaseKey];
            return { ...a, pianificazioneOffsetByMonth: current };
          }),
        };
      });

      await updateDocument(cliente.id, { manutenzioni: updatedManutenzioni });
      setPlannerOffsetFeedback({
        type: "success",
        message: "Pianificazione ripristinata correttamente.",
      });
    } catch (e) {
      setPlannerOffsetFeedback({
        type: "error",
        message: "Errore durante il ripristino della pianificazione.",
      });
    } finally {
      setPlannerOffsetSaving(false);
    }
  };

  return (
    <ResTecnicoAccess>
      <div className="contratti-clienti">
        <div className="cc-header">
          <div>
            <h1>Tabella Manutenzioni</h1>
            <p className="cc-subtitle">
              Consulta le manutenzioni programmate da contratto e applica offset
              puntuali alle attivita pianificate.
            </p>
          </div>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="cc-planner">
          <div className="cc-planner-toolbar">
            <div className="cc-planner-controls">
              <div className="cc-field cc-planner-year">
                <label>Anno</label>
                <input
                  type="number"
                  value={plannerYear}
                  onChange={(e) => setPlannerYear(Number(e.target.value))}
                />
              </div>
              <label className="cc-checkbox cc-planner-only-active">
                <input
                  type="checkbox"
                  checked={plannerOnlyActive}
                  onChange={(e) => setPlannerOnlyActive(e.target.checked)}
                />
                Solo attive
              </label>
            </div>
            <div className="cc-planner-hint">
              Rosso = attivita prevista senza intervento. Verde = attivita
              completata.
            </div>
          </div>

          {plannerRows.length === 0 ? (
            <div className="cc-empty-block" style={{ textAlign: "center" }}>
              Nessuna manutenzione da mostrare.
            </div>
          ) : (
            <div className="cc-planner-client-list">
              {plannerGroups.map((group) => {
                const current = plannerOpenByCliente[group.key];
                const isOpen = typeof current === "boolean" ? current : true;

                return (
                  <div key={group.key} className="cc-planner-client-section">
                    <div
                      className="cc-planner-client-header"
                      role="button"
                      tabIndex={0}
                      onClick={() => togglePlannerCliente(group.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          togglePlannerCliente(group.key);
                        }
                      }}
                    >
                      <span className="cc-planner-client-header-title">
                        {group.clienteNome}
                      </span>
                      <span className="cc-planner-client-header-chevron">
                        {isOpen ? "▾" : "▸"}
                      </span>
                    </div>

                    {isOpen && (
                      <div className="cc-planner-table-wrap">
                        <table className="cc-planner-table">
                          <thead>
                            <tr>
                              <th className="cc-planner-col-main">
                                Sede / Attivita
                              </th>
                              {mesi.map((m) => (
                                <th key={m} className="cc-planner-col-month">
                                  {m}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {group.rows.map((row) => (
                              <tr key={row.rowId}>
                                <td className="cc-planner-col-main">
                                  <div className="cc-planner-row-title">
                                    <div className="cc-planner-client">
                                      {row.sede || "—"}
                                    </div>
                                    <div className="cc-planner-meta">
                                      {row.attivitaDescrizione || "—"}
                                    </div>
                                  </div>
                                </td>
                                {mesi.map((_, monthIndex) => {
                                  const stats = getMonthStats(
                                    row,
                                    plannerYear,
                                    monthIndex,
                                  );
                                  const hasAny = stats.interventiCount > 0;
                                  let cellVariant = "empty";
                                  if (stats.expected && !hasAny) {
                                    cellVariant = "due";
                                  }
                                  if (stats.expected && hasAny) {
                                    cellVariant = "done";
                                  }

                                  const isSelected =
                                    selectedPlannerCell?.rowId === row.rowId &&
                                    selectedPlannerCell?.monthIndex ===
                                      monthIndex &&
                                    selectedPlannerCell?.year === plannerYear;

                                  return (
                                    <td key={`${row.rowId}__${monthIndex}`}>
                                      <button
                                        type="button"
                                        className={`cc-planner-cell cc-planner-${cellVariant} ${
                                          isSelected ? "selected" : ""
                                        }`}
                                        onClick={() =>
                                          setSelectedPlannerCell({
                                            rowId: row.rowId,
                                            monthIndex,
                                            year: plannerYear,
                                            currentMonthKey: toMonthKey(
                                              plannerYear,
                                              monthIndex,
                                            ),
                                            baseExpected: stats.baseExpected,
                                            movedHereFrom: stats.movedHereFrom,
                                            row,
                                          })
                                        }
                                        aria-label={`Dettagli ${mesi[monthIndex]} ${plannerYear}`}
                                        title={
                                          stats.expected
                                            ? hasAny
                                              ? "Completata (intervento registrato)"
                                              : "Da completare (nessun intervento)"
                                            : hasAny
                                              ? "Intervento extra"
                                              : ""
                                        }
                                      >
                                        {stats.expected && !hasAny && (
                                          <CircleDashed
                                            size={18}
                                            className="cc-planner-icon cc-planner-icon-due"
                                            style={{ color: "#E53935" }}
                                          />
                                        )}
                                        {stats.expected && hasAny && (
                                          <CheckCircle2
                                            size={18}
                                            className="cc-planner-icon cc-planner-icon-done"
                                            style={{ color: "#28A745" }}
                                          />
                                        )}
                                        {!stats.expected && hasAny && (
                                          <CircleDashed
                                            size={18}
                                            className="cc-planner-icon cc-planner-icon-extra"
                                            style={{ color: "#F57F17" }}
                                          />
                                        )}
                                      </button>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {selectedPlannerCell?.row && (
            <div
              className="cc-modal-overlay"
              onClick={() => setSelectedPlannerCell(null)}
            >
              <div
                className="cc-modal cc-planner-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                {(() => {
                  const { row, monthIndex, year } = selectedPlannerCell;
                  const stats = getMonthStats(row, year, monthIndex);
                  return (
                    <>
                      <div className="cc-modal-header">
                        <div>
                          <h2>
                            {row.clienteNome} • {mesi[monthIndex]} {year}
                          </h2>
                          <div className="cc-planner-modal-subtitle">
                            {(row.sede || "—") +
                              " • " +
                              (row.attivitaDescrizione || "—")}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="cc-icon-btn"
                          onClick={() => setSelectedPlannerCell(null)}
                          aria-label="Chiudi"
                          title="Chiudi"
                        >
                          ×
                        </button>
                      </div>

                      <div className="cc-modal-body">
                        <h3 className="cc-section-title">Interventi</h3>
                        {stats.interventi.length === 0 ? (
                          <div className="cc-empty-block">
                            Nessun intervento.
                          </div>
                        ) : (
                          <ul className="cc-planner-details-list">
                            {stats.interventi.map((i) => (
                              <li key={i.id}>
                                {i.data || "—"} • {i.descrizione || "—"}{" "}
                                {i.eseguitoDa ? `• ${i.eseguitoDa}` : ""}
                              </li>
                            ))}
                          </ul>
                        )}

                        {plannerOffsetBaseKey && (
                          <>
                            <h3 className="cc-section-title">Pianificazione</h3>
                            <div className="cc-grid-2">
                              <div className="cc-field">
                                <label>Prevista</label>
                                <input
                                  type="text"
                                  value={monthKeyToLabel(plannerOffsetBaseKey)}
                                  disabled
                                />
                              </div>
                              <div className="cc-field">
                                <label>Spostata a</label>
                                <input
                                  type="month"
                                  value={plannerOffsetTargetKey}
                                  onChange={(e) => {
                                    setPlannerOffsetTargetKey(e.target.value);
                                    setPlannerOffsetFeedback(null);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="cc-footer-actions cc-planner-offset-actions">
                              <button
                                type="button"
                                className="cc-btn cc-btn-primary"
                                disabled={
                                  plannerOffsetSaving || !plannerOffsetTargetKey
                                }
                                onClick={savePlannerOffset}
                              >
                                Salva spostamento
                              </button>
                              <button
                                type="button"
                                className="cc-btn cc-btn-secondary"
                                disabled={
                                  plannerOffsetSaving ||
                                  !plannerOffsetTargetKey ||
                                  plannerOffsetTargetKey ===
                                    plannerOffsetBaseKey
                                }
                                onClick={deletePlannerOffset}
                              >
                                Ripristina
                              </button>
                            </div>
                            {plannerOffsetFeedback?.message && (
                              <div
                                className={`cc-planner-offset-feedback ${
                                  plannerOffsetFeedback.type === "success"
                                    ? "success"
                                    : "error"
                                }`}
                              >
                                {plannerOffsetFeedback.message}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div className="cc-modal-footer">
                        <span />
                        <div className="cc-footer-actions">
                          <button
                            type="button"
                            className="cc-btn cc-btn-secondary"
                            onClick={() => setSelectedPlannerCell(null)}
                          >
                            Chiudi
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </ResTecnicoAccess>
  );
}
